<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { startOfMonth, endOfDay } from 'date-fns';
import { useStore } from 'dashboard/composables/store';
import { useSalesStore } from 'dashboard/stores/sales';
import { useSalesGoalsStore } from 'dashboard/stores/salesGoals';
import { useSaleTypesStore } from 'dashboard/stores/saleTypes';
import { useSalesTeamsStore } from 'dashboard/stores/salesTeams';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import AgentSalesCard from '../components/AgentSalesCard.vue';
import GoalProgressBar from '../components/GoalProgressBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import GoalCalendarModal from '../components/GoalCalendarModal.vue';
import AgentSalesModal from '../components/AgentSalesModal.vue';
import FollowUpsTab from '../components/FollowUpsTab.vue';
import MySalesTab from '../components/MySalesTab.vue';
import SaleTypeManagerModal from '../components/SaleTypeManagerModal.vue';
import SalesTeamManagerModal from '../components/SalesTeamManagerModal.vue';

const { t } = useI18n();
const store = useStore();
const salesStore = useSalesStore();
const goalsStore = useSalesGoalsStore();
const saleTypesStore = useSaleTypesStore();
const salesTeamsStore = useSalesTeamsStore();
const { isAdmin } = useAdmin();
const showSettingsMenu = ref(false);
const selectedTeamId = ref(null);

const goalCalendarModalRef = ref(null);
const agentSalesModalRef = ref(null);
const saleTypeManagerModalRef = ref(null);
const salesTeamManagerModalRef = ref(null);
const activeTab = ref('dashboard');

const now = new Date();
const dateRange = ref([startOfMonth(now), endOfDay(now)]);
const dateRangeType = ref(DATE_RANGE_TYPES.MONTH_TO_DATE);

const brl = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

const dashboard = computed(() => salesStore.getDashboard);
const isLoading = computed(() => salesStore.uiFlags.fetchingDashboard);
const isManager = computed(() => ['manager', 'team_manager'].includes(dashboard.value?.role));

const periodParams = computed(() => ({
  since: Math.floor(dateRange.value[0].getTime() / 1000),
  until: Math.floor(dateRange.value[1].getTime() / 1000),
  ...(selectedTeamId.value ? { team_id: selectedTeamId.value } : {}),
}));

function onDateRangeChanged([start, end, rangeType]) {
  dateRange.value = [start, end];
  dateRangeType.value = rangeType;
  loadDashboard();
}

const agentData = computed(() => {
  if (!dashboard.value || isManager.value) return null;
  return dashboard.value.data;
});

const managerData = computed(() => {
  if (!dashboard.value || !isManager.value) return null;
  return dashboard.value;
});

const saleTypeBreakdown = computed(() => {
  if (agentData.value) return agentData.value.sale_type_breakdown || [];
  if (managerData.value) return managerData.value.team?.sale_type_breakdown || [];
  return [];
});

const maxTypeTotal = computed(() =>
  saleTypeBreakdown.value.reduce((max, r) => Math.max(max, r.total), 0)
);

const typePct = total =>
  maxTypeTotal.value > 0 ? Math.round((total / maxTypeTotal.value) * 100) : 0;

const loadDashboard = () => salesStore.fetchDashboard(periodParams.value);

const openGoalForm = ({ agentId, agentName } = {}) =>
  goalCalendarModalRef.value?.open(agentId ?? null, agentName ?? '');
const openAgentSales = agent => agentSalesModalRef.value?.open(agent, periodParams.value);

onMounted(() => {
  loadDashboard();
  if (isAdmin.value) {
    goalsStore.fetchGoals();
    store.dispatch('agents/get');
    salesTeamsStore.fetch();
  }
  saleTypesStore.fetch();
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <!-- Header -->
    <header class="sticky top-0 z-10 px-6 bg-n-surface-1 border-b border-n-weak">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center justify-between w-full h-16 gap-4">
          <div class="flex items-center gap-4">
            <h1 class="text-xl font-medium text-n-slate-12">
              {{ $t('SALES.DASHBOARD.TITLE') }}
            </h1>
            <!-- Tab switcher -->
            <div class="flex rounded-lg border border-n-strong overflow-hidden">
              <button
                class="px-3 py-1.5 text-xs font-medium transition-colors"
                :class="activeTab === 'dashboard' ? 'bg-n-brand text-white' : 'text-n-slate-11 hover:bg-n-alpha-2'"
                @click="activeTab = 'dashboard'"
              >
                {{ $t('SALES.DASHBOARD.TITLE') }}
              </button>
              <button
                class="px-3 py-1.5 text-xs font-medium transition-colors"
                :class="activeTab === 'followups' ? 'bg-n-brand text-white' : 'text-n-slate-11 hover:bg-n-alpha-2'"
                @click="activeTab = 'followups'"
              >
                {{ $t('SALES.FOLLOW_UP.TAB') }}
              </button>
              <button
                class="px-3 py-1.5 text-xs font-medium transition-colors"
                :class="activeTab === 'my_sales' ? 'bg-n-brand text-white' : 'text-n-slate-11 hover:bg-n-alpha-2'"
                @click="activeTab = 'my_sales'"
              >
                {{ $t('SALES.MY_SALES.TAB') }}
              </button>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <WootDatePicker
              v-if="activeTab === 'dashboard'"
              v-model:date-range="dateRange"
              v-model:range-type="dateRangeType"
              @date-range-changed="onDateRangeChanged"
            />

            <!-- Menu de configurações (admin) -->
            <div v-if="isAdmin" class="relative">
              <button
                class="p-1.5 rounded-lg text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                :title="$t('SALES.DASHBOARD.SETTINGS')"
                @click="showSettingsMenu = !showSettingsMenu"
              >
                <span class="i-lucide-settings text-base" />
              </button>
              <div
                v-if="showSettingsMenu"
                v-on-clickaway="() => showSettingsMenu = false"
                class="absolute top-9 right-0 z-30 w-52 rounded-xl border border-n-strong bg-n-surface-1 shadow-lg py-1"
              >
                <button
                  class="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
                  @click="openGoalForm(); showSettingsMenu = false"
                >
                  <span class="i-lucide-target text-sm text-n-slate-9" />
                  {{ $t('SALES.DASHBOARD.MANAGE_GOALS') }}
                </button>
                <button
                  class="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
                  @click="saleTypeManagerModalRef?.open(); showSettingsMenu = false"
                >
                  <span class="i-lucide-tag text-sm text-n-slate-9" />
                  {{ $t('SALES.SALE_TYPE.MANAGE') }}
                </button>
                <button
                  class="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
                  @click="salesTeamManagerModalRef?.open(); showSettingsMenu = false"
                >
                  <span class="i-lucide-users text-sm text-n-slate-9" />
                  {{ $t('SALES.SALES_TEAM.CONFIGURE') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Main content -->
    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl mx-auto py-6">

        <!-- Aba Retornos -->
        <FollowUpsTab v-if="activeTab === 'followups'" />

        <!-- Aba Minhas Vendas -->
        <MySalesTab v-else-if="activeTab === 'my_sales'" :period-params="periodParams" />

        <!-- Loading -->
        <div v-if="activeTab === 'dashboard' && isLoading" class="flex items-center justify-center py-20">
          <span class="i-lucide-loader-circle animate-spin text-3xl text-n-slate-9" />
        </div>

        <!-- Visão do AGENTE -->
        <template v-else-if="activeTab === 'dashboard' && agentData">
          <!-- Banner de meta -->
          <div
            v-if="agentData.goal"
            class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 mb-6 flex flex-col gap-3"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-semibold text-n-slate-12">
                  <span v-if="agentData.goal.percentage >= 100">
                    {{ $t('SALES.GOAL_BANNER.MET') }}
                  </span>
                  <span v-else>
                    {{ $t('SALES.GOAL_BANNER.REMAINING_LABEL') }}
                    <strong>
                      {{ agentData.goal.goal_type === 'quantity'
                        ? agentData.goal.remaining
                        : brl(agentData.goal.remaining) }}
                    </strong>
                    {{ $t('SALES.GOAL_BANNER.TO_GO') }}
                  </span>
                </p>
                <p class="text-xs mt-0.5 opacity-70">
                  {{ $t('SALES.GOAL_BANNER.REALIZED') }}
                  {{ agentData.goal.goal_type === 'quantity'
                    ? agentData.goal.realized
                    : brl(agentData.goal.realized) }}
                  {{ $t('SALES.GOAL_BANNER.OF') }}
                  {{ agentData.goal.goal_type === 'quantity'
                    ? agentData.goal.goal_value
                    : brl(agentData.goal.goal_value) }}
                </p>
              </div>
              <span
                class="text-2xl font-bold text-n-slate-12"
              >
                {{ agentData.goal.percentage }}%
              </span>
            </div>
            <div class="h-2 rounded-full bg-white bg-opacity-60">
              <div
                class="h-full rounded-full transition-all duration-700"
                :class="{
                  'bg-n-teal-9': agentData.goal.percentage >= 100,
                  'bg-n-amber-9': agentData.goal.percentage >= 50 && agentData.goal.percentage < 100,
                  'bg-n-ruby-9': agentData.goal.percentage < 50,
                }"
                :style="{ width: `${Math.min(agentData.goal.percentage, 100)}%` }"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center">
              <p class="text-3xl font-bold text-n-slate-12">{{ agentData.total_resolved }}</p>
              <p class="text-xs text-n-slate-10 mt-1.5">{{ $t('SALES.STATS.TOTAL_RESOLVED') }}</p>
            </div>
            <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center">
              <p class="text-3xl font-bold text-n-slate-12">{{ agentData.total_with_sale }}</p>
              <p class="text-xs text-n-slate-10 mt-1.5">{{ $t('SALES.STATS.WITH_SALE') }}</p>
            </div>
            <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center">
              <p class="text-3xl font-bold text-n-slate-12">{{ agentData.total_without_sale }}</p>
              <p class="text-xs text-n-slate-10 mt-1.5">{{ $t('SALES.STATS.WITHOUT_SALE') }}</p>
            </div>
            <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center">
              <p class="text-3xl font-bold text-n-slate-12">{{ brl(agentData.total_amount) }}</p>
              <p class="text-xs text-n-slate-10 mt-1.5">{{ $t('SALES.STATS.TOTAL_AMOUNT') }}</p>
            </div>
          </div>

          <!-- Breakdown por tipo de venda (agente) -->
          <div v-if="saleTypeBreakdown.length" class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 mb-4">
            <h2 class="text-sm font-semibold text-n-slate-12 mb-4">{{ $t('SALES.SALE_TYPE.BREAKDOWN_TITLE') }}</h2>
            <div class="flex flex-col gap-3">
              <div v-for="item in saleTypeBreakdown" :key="item.type" class="flex flex-col gap-1.5">
                <div class="flex items-center justify-between text-xs">
                  <span class="text-n-slate-11 font-medium">{{ item.type }}</span>
                  <span class="text-n-slate-10">{{ item.count }} venda{{ item.count !== 1 ? 's' : '' }} · {{ brl(item.total) }}</span>
                </div>
                <div class="h-1.5 rounded-full bg-n-weak overflow-hidden">
                  <div
                    class="h-full bg-n-brand rounded-full transition-all duration-500"
                    :style="{ width: typePct(item.total) + '%' }"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 mb-4">
            <h2 class="text-sm font-semibold text-n-slate-12 mb-4">{{ $t('SALES.GOAL.MY_GOAL') }}</h2>
            <GoalProgressBar :goal="agentData.goal" />
          </div>

          <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-5">
            <h2 class="text-sm font-semibold text-n-slate-12 mb-4">{{ $t('SALES.CHART.DAILY_TITLE') }}</h2>
            <div v-if="agentData.daily_sales?.length" class="flex flex-col gap-1">
              <div
                v-for="day in agentData.daily_sales"
                :key="day.date"
                class="flex justify-between items-center text-sm py-2.5 border-b border-n-weak last:border-0"
              >
                <span class="text-n-slate-11">{{ day.date }}</span>
                <span class="text-n-slate-12 font-medium">
                  {{ day.count }} vendas · {{ brl(day.amount) }}
                </span>
              </div>
            </div>
            <p v-else class="text-sm text-n-slate-9">{{ $t('SALES.CHART.NO_DATA') }}</p>
          </div>
        </template>

        <!-- Visão do GESTOR (admin ou gerente de time) -->
        <template v-else-if="activeTab === 'dashboard' && managerData">
          <!-- Nome do time (só para team_manager) -->
          <div v-if="managerData.role === 'team_manager'" class="mb-4 flex items-center gap-2">
            <span class="i-lucide-users text-base text-n-slate-9" />
            <h2 class="text-base font-semibold text-n-slate-12">{{ managerData.team_name }}</h2>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <!-- Meta individual do gestor -->
            <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-5">
              <h2 class="text-sm font-semibold text-n-slate-12 mb-4">
                {{ $t('SALES.GOAL.MANAGER_INDIVIDUAL') }}
              </h2>
              <div class="flex gap-6 mb-4">
                <div>
                  <p class="text-2xl font-bold text-n-slate-12">{{ managerData.manager.total_with_sale }}</p>
                  <p class="text-xs text-n-slate-10 mt-1">{{ $t('SALES.STATS.WITH_SALE') }}</p>
                </div>
                <div>
                  <p class="text-2xl font-bold text-n-slate-12">{{ brl(managerData.manager.total_amount) }}</p>
                  <p class="text-xs text-n-slate-10 mt-1">{{ $t('SALES.STATS.TOTAL_AMOUNT') }}</p>
                </div>
              </div>
              <GoalProgressBar :goal="managerData.manager.goal" />
            </div>

            <!-- Meta do time -->
            <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-5">
              <div class="flex justify-between items-center mb-4">
                <h2 class="text-sm font-semibold text-n-slate-12">
                  {{ $t('SALES.GOAL.TEAM_GOAL') }}
                </h2>
                <span class="text-xs px-2.5 py-1 rounded-full font-medium text-white bg-n-slate-9">
                  {{ managerData.team.all_goals_met ? $t('SALES.GOAL_STATUS.MET') : $t('SALES.GOAL_STATUS.IN_PROGRESS') }}
                </span>
              </div>
              <div class="flex gap-6 mb-4">
                <div>
                  <p class="text-2xl font-bold text-n-slate-12">{{ managerData.team.total_count }}</p>
                  <p class="text-xs text-n-slate-10 mt-1">{{ $t('SALES.STATS.WITH_SALE') }}</p>
                </div>
                <div>
                  <p class="text-2xl font-bold text-n-slate-12">{{ brl(managerData.team.total_amount) }}</p>
                  <p class="text-xs text-n-slate-10 mt-1">{{ $t('SALES.STATS.TOTAL_AMOUNT') }}</p>
                </div>
              </div>
              <!-- Barra de meta agregada (visível para team_manager quando há metas) -->
              <GoalProgressBar
                v-if="managerData.team.team_goal"
                :goal="managerData.team.team_goal"
              />
            </div>
          </div>

          <!-- Breakdown por tipo de venda (gestor) -->
          <div v-if="saleTypeBreakdown.length" class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 mb-6">
            <h2 class="text-sm font-semibold text-n-slate-12 mb-4">{{ $t('SALES.SALE_TYPE.BREAKDOWN_TITLE') }}</h2>
            <div class="flex flex-col gap-3">
              <div v-for="item in saleTypeBreakdown" :key="item.type" class="flex flex-col gap-1.5">
                <div class="flex items-center justify-between text-xs">
                  <span class="text-n-slate-11 font-medium">{{ item.type }}</span>
                  <span class="text-n-slate-10">{{ item.count }} venda{{ item.count !== 1 ? 's' : '' }} · {{ brl(item.total) }}</span>
                </div>
                <div class="h-1.5 rounded-full bg-n-weak overflow-hidden">
                  <div
                    class="h-full bg-n-brand rounded-full transition-all duration-500"
                    :style="{ width: typePct(item.total) + '%' }"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Cards por agente -->
          <div v-if="managerData.team.agents?.length">
            <div class="flex items-center justify-between mb-3">
              <h2 class="text-sm font-semibold text-n-slate-12">
                {{ $t('SALES.DASHBOARD.AGENTS_SECTION') }}
              </h2>
              <select
                v-if="isAdmin && salesTeamsStore.teams?.length"
                v-model="selectedTeamId"
                class="w-auto shrink-0 self-center h-7 !py-0 px-2 leading-7 text-xs rounded border border-n-strong bg-n-surface-1 text-n-slate-11 focus:outline-none cursor-pointer"
                @change="loadDashboard"
              >
                <option :value="null">{{ $t('SALES.DASHBOARD.ALL_TEAMS') }}</option>
                <option v-for="team in salesTeamsStore.teams" :key="team.id" :value="team.id">
                  {{ team.name }}
                </option>
              </select>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <AgentSalesCard
                v-for="agent in managerData.team.agents"
                :key="agent.agent_id"
                :agent="agent"
                :is-admin="isAdmin"
                @set-goal="openGoalForm"
                @view-sales="openAgentSales"
              />
            </div>
          </div>
        </template>

      </div>
    </main>

    <GoalCalendarModal ref="goalCalendarModalRef" @saved="loadDashboard" />
    <AgentSalesModal ref="agentSalesModalRef" />
    <SaleTypeManagerModal ref="saleTypeManagerModalRef" />
    <SalesTeamManagerModal ref="salesTeamManagerModalRef" />
  </section>
</template>
